/**
 * CandleScript - Storage Service
 * Save/load indicator configurations to localStorage
 */

const STORAGE_KEY = 'candlescript_indicators';
const STORAGE_VERSION = '1.0.0';

/**
 * Get all saved indicators
 * @returns {Array<Object>}
 */
export function loadAllIndicators() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const data = JSON.parse(raw);
        if (data.version !== STORAGE_VERSION) {
            // Migration could happen here
            return data.indicators || [];
        }
        return data.indicators || [];
    } catch (e) {
        console.warn('[CandleScript] Failed to load indicators:', e);
        return [];
    }
}

/**
 * Save all indicators
 * @param {Array<Object>} indicators
 */
export function saveAllIndicators(indicators) {
    try {
        const data = {
            version: STORAGE_VERSION,
            indicators,
            updatedAt: Date.now(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        return true;
    } catch (e) {
        console.warn('[CandleScript] Failed to save indicators:', e);
        return false;
    }
}

/**
 * Save a single indicator (upsert)
 * @param {Object} config - Indicator config
 */
export function saveIndicator(config) {
    const all = loadAllIndicators();
    const idx = all.findIndex(i => i.id === config.id);
    config.updatedAt = Date.now();
    if (idx >= 0) {
        all[idx] = config;
    } else {
        all.push(config);
    }
    return saveAllIndicators(all);
}

/**
 * Delete an indicator by ID
 * @param {string} id
 */
export function deleteIndicator(id) {
    const all = loadAllIndicators();
    const filtered = all.filter(i => i.id !== id);
    return saveAllIndicators(filtered);
}

/**
 * Get a single indicator by ID
 * @param {string} id
 * @returns {Object|null}
 */
export function getIndicator(id) {
    const all = loadAllIndicators();
    return all.find(i => i.id === id) || null;
}

/**
 * Export indicator as downloadable JSON
 * @param {Object} config
 */
export function exportIndicator(config) {
    const json = JSON.stringify(config, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.name.replace(/\s+/g, '_').toLowerCase()}.candle.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Import indicator from JSON file
 * @returns {Promise<Object>}
 */
export function importIndicator() {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,.candle.json';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return reject(new Error('No file selected'));
            const reader = new FileReader();
            reader.onload = (ev) => {
                try {
                    const config = JSON.parse(ev.target.result);
                    resolve(config);
                } catch (err) {
                    reject(new Error('Invalid JSON file'));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        };
        input.click();
    });
}

export default {
    loadAllIndicators,
    saveAllIndicators,
    saveIndicator,
    deleteIndicator,
    getIndicator,
    exportIndicator,
    importIndicator,
};
